import React, { useState, useEffect, useCallback } from 'react';
import {
  Box,
  Typography,
  Tabs,
  Tab,
  Grid,
  Paper,
  CircularProgress,
  Alert,
  Button,
  Skeleton,
  Divider
} from '@mui/material';
import { Close, Refresh } from '@mui/icons-material';
import axios from 'axios';
import ClientStatusHero from './ClientStatusHero';
import ClientCurrentSectorCard from './ClientCurrentSectorCard';
import ClientCharterPanel from './ClientCharterPanel';
import ClientMaintenancePanel from './ClientMaintenancePanel';
import ClientUtilizationCharts from './ClientUtilizationCharts';
import ClientDocumentsPanel from './ClientDocumentsPanel';
import {
  COLORS,
  glassCardSx,
  accentBarSx,
  pillPrimarySx,
  pillSecondarySx,
  pillDangerSx,
  sectionTitleSx,
  alertInfoSx
} from './clientPortalTheme';

const formatDate = (value) => {
  if (!value) return '—';
  return new Date(value).toLocaleDateString();
};

const ClientAircraftDetail = ({ aircraftId, onClose }) => {
  const [tab, setTab] = useState(0);
  const [overview, setOverview] = useState(null);
  const [charters, setCharters] = useState(null);
  const [maintenance, setMaintenance] = useState(null);
  const [utilization, setUtilization] = useState(null);
  const [documents, setDocuments] = useState([]);
  const [loading, setLoading] = useState(true);
  const [tabLoading, setTabLoading] = useState({});
  const [error, setError] = useState(null);

  useEffect(() => {
    setTab(0);
    setOverview(null);
    setCharters(null);
    setMaintenance(null);
    setUtilization(null);
    setDocuments([]);
    setError(null);
  }, [aircraftId]);

  const fetchOverview = useCallback(async () => {
    const response = await axios.get(`/api/client-portal/aircraft/${aircraftId}/overview`);
    setOverview(response.data);
    return response.data;
  }, [aircraftId]);

  const fetchDocuments = useCallback(async () => {
    const response = await axios.get(`/api/client-portal/aircraft/${aircraftId}`);
    setDocuments(response.data.documents || []);
  }, [aircraftId]);

  const fetchCharters = useCallback(async () => {
    setTabLoading((p) => ({ ...p, charters: true }));
    try {
      const response = await axios.get(`/api/client-portal/aircraft/${aircraftId}/charters`);
      setCharters(response.data);
    } finally {
      setTabLoading((p) => ({ ...p, charters: false }));
    }
  }, [aircraftId]);

  const fetchMaintenance = useCallback(async () => {
    setTabLoading((p) => ({ ...p, maintenance: true }));
    try {
      const response = await axios.get(`/api/client-portal/aircraft/${aircraftId}/maintenance`);
      setMaintenance(response.data);
    } finally {
      setTabLoading((p) => ({ ...p, maintenance: false }));
    }
  }, [aircraftId]);

  const fetchUtilization = useCallback(async () => {
    setTabLoading((p) => ({ ...p, utilization: true }));
    try {
      const response = await axios.get(`/api/client-portal/aircraft/${aircraftId}/utilization`);
      setUtilization(response.data);
    } finally {
      setTabLoading((p) => ({ ...p, utilization: false }));
    }
  }, [aircraftId]);

  const loadAll = useCallback(async () => {
    setLoading(true);
    setError(null);
    try {
      await Promise.all([fetchOverview(), fetchDocuments()]);
    } catch (err) {
      console.error('Aircraft detail load error:', err);
      setError('Failed to load aircraft details.');
    } finally {
      setLoading(false);
    }
  }, [fetchOverview, fetchDocuments]);

  useEffect(() => {
    if (!aircraftId) return undefined;
    loadAll();
    const interval = setInterval(() => {
      fetchOverview().catch(() => {});
    }, 60000);
    return () => clearInterval(interval);
  }, [aircraftId, loadAll, fetchOverview]);

  useEffect(() => {
    if (!aircraftId) return;
    if (tab === 1 && !charters) fetchCharters();
    if (tab === 2 && !maintenance) fetchMaintenance();
    if (tab === 3 && !utilization) fetchUtilization();
  }, [tab, aircraftId, charters, maintenance, utilization, fetchCharters, fetchMaintenance, fetchUtilization]);

  const handleDocumentDownload = async (docAircraftId, docId, fileName) => {
    try {
      const response = await axios.get(
        `/api/client-portal/aircraft/${docAircraftId}/documents/${docId}/download`,
        { responseType: 'blob' }
      );
      const url = window.URL.createObjectURL(new Blob([response.data]));
      const link = document.createElement('a');
      link.href = url;
      link.setAttribute('download', fileName);
      document.body.appendChild(link);
      link.click();
      link.remove();
      window.URL.revokeObjectURL(url);
    } catch (err) {
      console.error('Download error:', err);
    }
  };

  if (!aircraftId) return null;

  const aircraft = overview?.aircraft;

  if (loading) {
    return (
      <Box sx={{ mt: 3 }}>
        <Skeleton variant="rectangular" height={160} sx={{ mb: 2, borderRadius: 4 }} />
        <Skeleton variant="rectangular" height={300} sx={{ borderRadius: 4 }} />
      </Box>
    );
  }

  if (error || !aircraft) {
    return (
      <Box sx={{ mt: 3 }}>
        <Alert severity="error" sx={{ borderRadius: 3, bgcolor: COLORS.redGlass, color: COLORS.text, border: `1px solid rgba(229,57,53,0.35)` }}>{error || 'Aircraft not found'}</Alert>
      </Box>
    );
  }

  const techLogUsage = overview?.techLogUsage || null;

  return (
    <Box sx={{ mt: 3 }} id="client-aircraft-detail">
      <Divider sx={{ mb: 3, borderColor: COLORS.glassBorder }} />
      <Box display="flex" justifyContent="space-between" alignItems="center" mb={2.5} flexWrap="wrap" gap={1.5}>
        <Box sx={accentBarSx}>
          <Typography variant="h5" fontWeight={800} sx={{ color: COLORS.text }}>Aircraft Details</Typography>
        </Box>
        <Box display="flex" gap={1}>
          <Button startIcon={<Refresh />} onClick={loadAll} size="small" sx={pillSecondarySx}>
            Refresh
          </Button>
          {onClose && (
            <Button startIcon={<Close />} onClick={onClose} size="small" sx={pillDangerSx}>
              Close
            </Button>
          )}
        </Box>
      </Box>

      <ClientStatusHero
        aircraft={aircraft}
        currentSector={overview.currentSector}
        activeCharter={overview.activeCharter}
      />

      <Paper
        elevation={0}
        sx={{
          ...glassCardSx,
          mb: 2.5,
          px: 1,
          py: 0.5
        }}
      >
        <Tabs
          value={tab}
          onChange={(_, v) => setTab(v)}
          sx={{
            minHeight: 48,
            '& .MuiTab-root': {
              fontWeight: 600,
              minHeight: 48,
              textTransform: 'none',
              color: COLORS.textMuted,
              borderRadius: 999,
              mx: 0.25,
              '&.Mui-selected': { color: COLORS.white, bgcolor: COLORS.orange, boxShadow: '0 4px 16px rgba(29, 78, 120, 0.28)' }
            },
            '& .MuiTabs-indicator': { display: 'none' }
          }}
          variant="scrollable"
          scrollButtons="auto"
        >
          {['Overview', 'Charters', 'Maintenance', 'Utilization', 'Documents'].map((label) => (
            <Tab key={label} label={label} />
          ))}
        </Tabs>
      </Paper>

      {tab === 0 && (
        <Box>
          <ClientCurrentSectorCard
            currentSector={overview.currentSector}
            activeCharter={overview.activeCharter}
          />
          <Paper elevation={0} sx={{ ...glassCardSx, p: 3 }}>
            <Box sx={{ ...accentBarSx, mb: 2 }}>
              <Typography variant="h6" fontWeight={800} sx={{ color: COLORS.text }}>Aircraft Specifications</Typography>
            </Box>
            <Grid container spacing={2}>
              <Grid item xs={12} md={4}>
                <Typography variant="caption" sx={{ color: COLORS.textMuted }}>Serial Number</Typography>
                <Typography sx={{ color: COLORS.text, fontWeight: 600 }}>{aircraft.serialNumber}</Typography>
              </Grid>
              <Grid item xs={12} md={4}>
                <Typography variant="caption" sx={{ color: COLORS.textMuted }}>Aircraft Type</Typography>
                <Typography sx={{ color: COLORS.text, fontWeight: 600 }}>{aircraft.aircraftType}</Typography>
              </Grid>
              <Grid item xs={12} md={4}>
                <Typography variant="caption" sx={{ color: COLORS.textMuted }}>Max Passengers</Typography>
                <Typography sx={{ color: COLORS.text, fontWeight: 600 }}>{aircraft.maxPassengers}</Typography>
              </Grid>
              <Grid item xs={12} md={4}>
                <Typography variant="caption" sx={{ color: COLORS.textMuted }}>Owner</Typography>
                <Typography sx={{ color: COLORS.text, fontWeight: 600 }}>{aircraft.owner || '—'}</Typography>
              </Grid>
              <Grid item xs={12} md={4}>
                <Typography variant="caption" sx={{ color: COLORS.textMuted }}>Operator</Typography>
                <Typography sx={{ color: COLORS.text, fontWeight: 600 }}>{aircraft.operator || '—'}</Typography>
              </Grid>
              <Grid item xs={12} md={4}>
                <Typography variant="caption" sx={{ color: COLORS.textMuted }}>Date of Manufacture</Typography>
                <Typography sx={{ color: COLORS.text, fontWeight: 600 }}>{formatDate(aircraft.dateOfManufacture)}</Typography>
              </Grid>
              <Grid item xs={12} md={4}>
                <Typography variant="caption" sx={{ color: COLORS.textMuted }}>Flight Hours</Typography>
                <Typography sx={{ color: COLORS.text, fontWeight: 600 }}>{techLogUsage?.periodValue ?? aircraft.flightHours ?? '—'}</Typography>
              </Grid>
              {techLogUsage && (
                <>
                  {techLogUsage.landings != null && (
                    <Grid item xs={12} md={4}>
                      <Typography variant="caption" sx={{ color: COLORS.textMuted }}>Landings (Tech Log)</Typography>
                      <Typography sx={{ color: COLORS.text, fontWeight: 600 }}>{techLogUsage.landings}</Typography>
                    </Grid>
                  )}
                  {techLogUsage.asOnDate && (
                    <Grid item xs={12} md={4}>
                      <Typography variant="caption" sx={{ color: COLORS.textMuted }}>Tech Log As On</Typography>
                      <Typography sx={{ color: COLORS.text, fontWeight: 600 }}>{formatDate(techLogUsage.asOnDate)}</Typography>
                    </Grid>
                  )}
                  {techLogUsage.sheetNumber && (
                    <Grid item xs={12} md={4}>
                      <Typography variant="caption" sx={{ color: COLORS.textMuted }}>Tech Log Sheet #</Typography>
                      <Typography sx={{ color: COLORS.text, fontWeight: 600 }}>{techLogUsage.sheetNumber}</Typography>
                    </Grid>
                  )}
                </>
              )}
              <Grid item xs={12} md={4}>
                <Typography variant="caption" sx={{ color: COLORS.textMuted }}>Open Work Orders</Typography>
                <Typography sx={{ color: COLORS.text, fontWeight: 600 }}>{overview.openWorkOrderCount ?? 0}</Typography>
              </Grid>
              {overview.nextMaintenanceDue && (
                <Grid item xs={12} md={4}>
                  <Typography variant="caption" sx={{ color: COLORS.textMuted }}>Next Maintenance Due</Typography>
                  <Typography sx={{ color: COLORS.text, fontWeight: 600 }}>
                    {formatDate(overview.nextMaintenanceDue.dueDate)}
                    {overview.nextMaintenanceDue.title && ` — ${overview.nextMaintenanceDue.title}`}
                  </Typography>
                </Grid>
              )}
              {aircraft.remarks && (
                <Grid item xs={12}>
                  <Typography variant="caption" sx={{ color: COLORS.textMuted }}>Remarks</Typography>
                  <Typography sx={{ color: COLORS.text }}>{aircraft.remarks}</Typography>
                </Grid>
              )}
            </Grid>
          </Paper>
        </Box>
      )}

      {tab === 1 && (
        tabLoading.charters
          ? <Box display="flex" justifyContent="center" py={4}><CircularProgress /></Box>
          : <ClientCharterPanel charters={charters} loading={tabLoading.charters} />
      )}

      {tab === 2 && (
        tabLoading.maintenance
          ? <Box display="flex" justifyContent="center" py={4}><CircularProgress /></Box>
          : <ClientMaintenancePanel maintenance={maintenance} loading={tabLoading.maintenance} />
      )}

      {tab === 3 && (
        tabLoading.utilization
          ? <Box display="flex" justifyContent="center" py={4}><CircularProgress /></Box>
          : <ClientUtilizationCharts utilization={utilization} loading={tabLoading.utilization} />
      )}

      {tab === 4 && (
        <ClientDocumentsPanel
          documents={documents}
          aircraftId={aircraftId}
          onDocumentDownload={handleDocumentDownload}
        />
      )}
    </Box>
  );
};

export default ClientAircraftDetail;
